import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { IApp } from '../Interfaces/IApp';
import {
  useGetAppSettingsByApp,
  useUpsertAppSettings,
} from '../hooks/reactQuerys/AppQueries';
import { useAlert } from '../context/AlertContext';

interface IProps {
  app: IApp & { idApp: number };
  open: boolean;
  onClose: () => void;
  onSaved?: () => void;
}

const isSecretKey = (key: string) => /key|password|token|secret/i.test(key);

export const ExtensionConfigDialog: React.FC<IProps> = ({
  app,
  open,
  onClose,
  onSaved,
}) => {
  const { showAlert } = useAlert();
  const { settings, isLoadingSettings, refetchSettings } =
    useGetAppSettingsByApp(open ? app.idApp : null);
  const { upsertSettings, isPendingUpsertSettings } = useUpsertAppSettings();

  const [draft, setDraft] = useState<Record<string, string>>({});

  useEffect(() => {
    if (settings) {
      const next: Record<string, string> = {};
      for (const s of settings) {
        next[s.AppKey] = s.AppValue ?? '';
      }
      setDraft(next);
    }
  }, [settings]);

  const handleSave = async () => {
    try {
      await upsertSettings({ idApp: app.idApp, values: draft });
      await refetchSettings();
      showAlert({
        text: 'Einstellungen gespeichert.',
        severity: 'success',
      });
      onSaved?.();
      onClose();
    } catch (error) {
      console.error(error);
      showAlert({
        text: 'Speichern fehlgeschlagen.',
        severity: 'error',
      });
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle>{app.App} konfigurieren</DialogTitle>
      <DialogContent dividers>
        {isLoadingSettings ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : settings && settings.length > 0 ? (
          <Stack spacing={2} sx={{ mt: 1 }}>
            {settings.map(s => (
              <TextField
                key={s.idAppSetting}
                label={s.Title?.trim() ? s.Title : s.AppKey}
                helperText={s.Description ?? s.DefaultValue ?? undefined}
                placeholder={s.DefaultValue ?? undefined}
                value={draft[s.AppKey] ?? ''}
                onChange={e =>
                  setDraft(prev => ({ ...prev, [s.AppKey]: e.target.value }))
                }
                type={isSecretKey(s.AppKey) ? 'password' : 'text'}
                fullWidth
              />
            ))}
          </Stack>
        ) : (
          <Typography color='text.secondary'>
            Diese Erweiterung bietet keine Einstellungen.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={isPendingUpsertSettings}>
          Abbrechen
        </Button>
        <Button
          onClick={handleSave}
          variant='contained'
          disabled={
            isPendingUpsertSettings ||
            isLoadingSettings ||
            !settings ||
            settings.length === 0
          }
        >
          {isPendingUpsertSettings ? (
            <Box display='flex' alignItems='center' gap={1}>
              <span>Speichern</span>
              <CircularProgress size={20} />
            </Box>
          ) : (
            'Speichern'
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
